import React from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';

const HeadComponent = ({ property: { name, branding, bodyFont, headerFont } }) => {
  const renderFontLinks = () => {
    if (!branding?.fontLocation) {
      return (
        <link
          href="https://fonts.googleapis.com/css?family=Open+Sans:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      );
    }

    if (branding.fontLocation === 'Google' && bodyFont && headerFont) {
      return (
        <>
          <link
            key="bodyFont"
            href={`https://fonts.googleapis.com/css?family=${bodyFont}:400,500,600,700&display=swap`}
            rel="stylesheet"
          />
          <link
            key="headerFont"
            href={`https://fonts.googleapis.com/css?family=${headerFont}:400,500,600,700&display=swap`}
            rel="stylesheet"
          />
        </>
      );
    }

    if (
      branding.fontLocation === 'Typekit' &&
      branding.typekitHeaderProjectId &&
      branding.typekitBodyProjectId
    ) {
      return (
        <>
          <link
            key="headerFont"
            href={`https://use.typekit.net/${branding.typekitHeaderProjectId}.css`}
            rel="stylesheet"
          />
          <link
            key="bodyFont"
            href={`https://use.typekit.net/${branding.typekitBodyProjectId}.css`}
            rel="stylesheet"
          />
        </>
      );
    }

    if (branding.fontLocation === 'Custom' && bodyFont && headerFont) {
      return _generateFontMetaTags(branding, bodyFont, headerFont);
    }

    return null;
  };

  return (
    <Head>
      <title key="title">{name}</title>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5, minimum-scale=1"
      />

      {renderFontLinks()}
    </Head>
  );
};

const _generateFontMetaTags = (branding, bodyFont, headerFont) => {
  const fontUrlKeys = [
    'headerFontNormalUrl',
    'headerFontMediumUrl',
    'headerFontSemiBoldUrl',
    'headerFontBoldUrl',
    'bodyFontNormalUrl',
    'bodyFontMediumUrl',
    'bodyFontMediumUrl',
    'bodyFontSemiBoldUrl',
    'bodyFontBoldUrl',
  ];
  const weightKeys = [
    'headerFontNormalWeight',
    'headerFontMediumWeight',
    'headerFontSemiBoldWeight',
    'headerFontBoldWeight',
    'bodyFontNormalWeight',
    'bodyFontMediumWeight',
    'bodyFontMediumWeight',
    'bodyFontSemiBoldWeight',
    'bodyFontBoldWeight',
  ];

  return fontUrlKeys.map((fontUrlKey, index) => {
    const fontUrl = branding[fontUrlKey];
    const fontWeight = branding[weightKeys[index]];
    const fontFamily = fontUrlKey.startsWith('headerFont') ? headerFont : bodyFont;

    return fontUrl ? (
      <>
        <link rel="stylesheet" href={fontUrl} type="text/css" />
        <style>
          {`@font-face { 
              font-family: ${fontFamily}; 
              src: url("${fontUrl}") format("woff"); 
              font-weight: ${fontWeight}; 
            }`}
        </style>
      </>
    ) : (
      ''
    );
  });
};

HeadComponent.propTypes = {
  property: PropTypes.shape({
    name: PropTypes.string,
    bodyFont: PropTypes.string,
    headerFont: PropTypes.string,
  }).isRequired,
};

export default HeadComponent;
